import { replaceUnderscoresWithSpaces, showToastMessage, validateNumberWithoutDecimal } from '../../../utils/commonFunctions';
import React, { useImperativeHandle, forwardRef, useState, useMemo, useEffect, useRef } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import './DynamicForm.css';
import CustomSelect from '../CustomSelect';
import CustomTimePicker from '../CustomTimePicker';
import _ from "lodash";
import dayjs, { Dayjs } from "dayjs";
import { CheckboxField, NumberField, TextField } from './DynamicFormFields';
import { flattenFields } from './dynamicFormUtils';

// Helper: get Select value option for enum field (uses loose equality for number/string mismatch)
function getEnumFieldSelectValue(field: any, value: any): { label: string; value: any } | null {
  const idx = field?.enum?.findIndex((opt: any) => opt == value);
  return idx >= 0 ? { label: field?.valuesLabel?.[idx] ?? '', value } : null;
}

// Helper: handle enum field Select change
function handleEnumFieldChange(field: any, selected: any, onChange: (value: any) => void): void {
  const selectedIndex = field?.valuesLabel?.indexOf(selected?.label);
  if (selectedIndex !== -1) {
    onChange(field?.enum?.[selectedIndex]);
  } else {
    onChange(null);
  }
}

// Main Form Component Interface
interface DynamicFormProps {
  supportedSystemFaults: any;
  postInject?: (faultScheduleId: any, data: any) => void | ((data: any) => void);
  faultScheduleId?: any;
  currentTime?: number;
  editingFault?: any;
  dashboard?: 'scenarioCreation' | 'simulation' | 'design' | string;
  duration?: any;
  simulation_time?: any;
  current_simulation_time?: number;
  onSelectionChange?: (system: string, faultType: string) => void;
  faultPlanEditSessionKey?: string;
}

const DynamicForm = forwardRef(function DynamicForm(
  {
    supportedSystemFaults,
    postInject,
    faultScheduleId,
    currentTime,
    duration,
    editingFault,
    dashboard,
    simulation_time,
    current_simulation_time,
    onSelectionChange,
    faultPlanEditSessionKey
  }: DynamicFormProps,
  ref
) {
  const lastFaultPlanSessionKeyRef = useRef<string | null>(null);

  // ==============================================
  // DASHBOARD-SPECIFIC LOGIC
  // ==============================================

  const dashboardMode = dashboard || 'simulation';

  // ==============================================
  // TIME HANDLING - COMPATIBLE WITH BOTH JS AND TS
  // ==============================================

  // Computes current simulation time from various time sources
  const currentSimulationTime = useMemo(() => {
    if (current_simulation_time !== undefined) {
      return current_simulation_time;
    } else if (currentTime !== undefined) {
      return currentTime * 1000;
    }
    return Date.now();
  }, [current_simulation_time, currentTime]);

  // Computes initial duration value from editingFault, duration prop, or simulation_time
  const initialDuration = useMemo(() => {
    if (editingFault?.duration !== undefined) return editingFault?.duration;
    if (duration !== undefined) return duration as number;
    if (simulation_time?.duration !== undefined) return simulation_time.duration;
    return 0;
  }, [editingFault?.duration, duration, simulation_time?.duration as number]);

  // Computes scenario end time based on start time and duration
  const endTime = useMemo(() => {
    if (simulation_time?.start_time && simulation_time?.duration) {
      return (simulation_time?.start_time as number * 1000) + (simulation_time?.duration as number * 60 * 1000);
    } else if (currentTime && duration) {
      return (currentTime as number * 1000) + (duration as number * 60 * 1000);
    }
    return currentSimulationTime + (initialDuration as number * 60 * 1000);
  }, [simulation_time, currentTime, duration, currentSimulationTime, initialDuration]);

  // ==============================================
  // SCHEMA PROCESSING
  // ==============================================

  // Flattens nested schema structure into flat field array for form rendering
  const precomputedFlattenedSchema = useMemo(() => {
    if (!supportedSystemFaults) return null;

    return Object?.entries(supportedSystemFaults).reduce((acc: any, [type, subtypes]: [string, any]) => {
      acc[type] = Object?.entries(subtypes).reduce((subAcc: any, [subtype, fault]: [string, any]) => {
        // Use InputFields if present, otherwise fallback to the top-level fields (for backward compatibility)
        const fieldsToFlatten = fault.InputFields || fault;
        // Pass fieldsToFlatten as parentInputFields for dynamic field detection
        subAcc[subtype] = flattenFields(fieldsToFlatten, `${type}.${subtype}`, fieldsToFlatten);
        return subAcc;
      }, {} as any);
      return acc;
    }, {} as any);
  }, [supportedSystemFaults]);


  // ==============================================
  // ENHANCED STATE MANAGEMENT FOR DASHBOARD MODES
  // ==============================================

  // Returns initial executeLater value based on dashboard mode and editingFault
  const getInitialExecuteLater = () => {
    if (editingFault?.executeLater !== undefined) return editingFault.executeLater;

    switch (dashboardMode) {
      case 'scenarioCreation':
        return true;
      case 'simulation':
        return false;
      case 'design':
        return true;
      default:
        return false;
    }
  };

  const [selectedType, setSelectedType] = useState<string>(editingFault?.system || '');
  const [selectedSubtype, setSelectedSubtype] = useState<string>(editingFault?.faultType || '');
  const [executeLater, setExecuteLater] = useState<boolean>(getInitialExecuteLater());
  const [durationChecked, setDurationChecked] = useState<boolean>(!!editingFault?.duration);
  const [durationValue, setDurationValue] = useState<number>(initialDuration);
  const [startTime, setStartTime] = useState<number>(
    editingFault?.faultExecutionTime ? editingFault.faultExecutionTime * 1000 : currentSimulationTime
  );


  const faultDetails = editingFault?.faultDetails;

  const methods = useForm({
    mode: 'onChange',
    defaultValues: faultDetails || {}
  });

  // ==============================================
  // MEMOIZED VALUES
  // ==============================================

  // Generates options array for system type dropdown
  const typeOptions = useMemo(() =>
    Object?.keys(precomputedFlattenedSchema || {})?.map((k: string) => ({
      value: k,
      label: replaceUnderscoresWithSpaces(k)
    }))
    , [precomputedFlattenedSchema]);

  // Generates options array for fault subtype dropdown based on selected type
  const subtypeOptions = useMemo(() => (
    selectedType && precomputedFlattenedSchema && precomputedFlattenedSchema[selectedType]
      ? Object?.keys(precomputedFlattenedSchema[selectedType] || {})?.map((k: string) => ({
        value: k,
        label: replaceUnderscoresWithSpaces(k)
      }))
      : []
  ), [selectedType, precomputedFlattenedSchema]);

  // Gets flattened fields array for selected type and subtype
  const fields = useMemo(() => (
    selectedType && selectedSubtype && precomputedFlattenedSchema && precomputedFlattenedSchema[selectedType][selectedSubtype]
      ? precomputedFlattenedSchema[selectedType][selectedSubtype] as any
      : []
  ), [selectedType, selectedSubtype, precomputedFlattenedSchema]);

  // Gets AllowedTriggerFields configuration for selected fault type
  const allowedTriggerFields: Record<string, boolean> | null = useMemo(() => {
    if (!supportedSystemFaults || !selectedType || !selectedSubtype) return null;
    const subtypeNode = supportedSystemFaults?.[selectedType]?.[selectedSubtype];
    return subtypeNode?.AllowedTriggerFields as Record<string, boolean> | null;
  }, [supportedSystemFaults, selectedType, selectedSubtype]);

  // Filters fields based on AllowedTriggerFields configuration
  const visibleFields = useMemo(() => {
    if (!allowedTriggerFields) return fields;
    return fields?.filter((f: any) => allowedTriggerFields[f.originalKey] !== false);
  }, [fields, allowedTriggerFields]);

  // Finds master boolean field that controls visibility of other fields
  const masterBooleanField = useMemo(() => (
    (visibleFields || fields)?.find((f: any) => f.type === 'bool')
  ), [visibleFields, fields]);

  const masterBooleanName = masterBooleanField?.name;
  const masterBooleanValue = masterBooleanName ? methods.watch(masterBooleanName as string) : true;

  // Filters and gets all dynamic fields that depend on other fields
  const dynamicFields = useMemo(() =>
    (visibleFields || fields)?.filter((f: any) => f.isDynamicField === true) || []
    , [visibleFields, fields]);

  // Watch all form values - this will trigger updates when any field changes
  const allFormValues = methods.watch();

  // Maps dependency field values to their corresponding dynamic fields
  const dependencyFieldValues = useMemo(() => {
    const values: Record<string, any> = {};
    dynamicFields.forEach((dynamicField: any) => {
      if (dynamicField.dependencyField?.originalKey) {
        // Find the actual dependency field in the fields array by originalKey
        const depField = (visibleFields || fields)?.find((f: any) =>
          f.originalKey === dynamicField.dependencyField.originalKey
        );

        if (depField?.name) {
          // Form state uses dotted names; in edit mode first render may still have flat faultDetails - fallback for options resolution
          const depValue = methods.getValues(depField.name as any)
            ?? faultDetails?.[dynamicField.dependencyField.originalKey];
          values[dynamicField.name] = depValue;
        }
      }
    });
    return values;
  }, [dynamicFields, allFormValues, visibleFields, fields, methods, faultDetails]);

  // Updates dynamic fields with correct enum/valuesLabel based on dependency field value
  const dynamicFieldsWithOptions = useMemo(() => {
    return dynamicFields.map((dynamicField: any) => {
      if (!dynamicField.dependencyField?.originalKey || !dynamicField.dynamicOptions) {
        return dynamicField;
      }

      const dependencyValue = dependencyFieldValues[dynamicField.name];

      if (dependencyValue === null || dependencyValue === undefined) {
        return {
          ...dynamicField,
          enum: [],
          valuesLabel: []
        };
      }

      const dependencyKey = String(dependencyValue);
      const optionsForValue = dynamicField.dynamicOptions[dependencyKey];

      if (optionsForValue && Array.isArray(optionsForValue.enum) && Array.isArray(optionsForValue.valuesLabel)) {
        return {
          ...dynamicField,
          enum: optionsForValue.enum,
          valuesLabel: optionsForValue.valuesLabel
        };
      }

      return {
        ...dynamicField,
        enum: [],
        valuesLabel: []
      };
    });
  }, [dynamicFields, dependencyFieldValues]);

  // Merges dynamic fields with updated options into visible fields and filters by visibility rules
  const finalVisibleFields = useMemo(() => {
    if (dynamicFieldsWithOptions.length === 0) return visibleFields;

    const dynamicFieldMap = new Map(
      dynamicFieldsWithOptions.map((f: any) => [f.name, f])
    );

    const mappedFields = visibleFields?.map((f: any) =>
      f.isDynamicField ? dynamicFieldMap.get(f.name) || f : f
    );

    const filteredFields = mappedFields?.filter((f: any) => {
      // Only show dynamic field if dependency is selected and has valid options
      if (f.isDynamicField) {
        const dependencyValue = dependencyFieldValues[f.name];
        return dependencyValue !== null &&
          dependencyValue !== undefined &&
          f.enum &&
          f.enum.length > 0;
      }
      return true;
    });

    return filteredFields;
  }, [visibleFields, dynamicFieldsWithOptions, dependencyFieldValues]);

  // ==============================================
  // CORRECTED EFFECTS FOR DASHBOARD MODES
  // ==============================================

  // Effect 1: Set initial executeLater based on dashboard mode
  useEffect(() => {
    if (!editingFault) {
      setExecuteLater(getInitialExecuteLater());
    }
  }, [dashboardMode, editingFault]);

  // Effect 2: Initialize form when editingFault / fault session changes
  useEffect(() => {
    if (!editingFault) {
      lastFaultPlanSessionKeyRef.current = null;
      return;
    }

    const faultDetailsLocal = editingFault?.faultDetails;

    setSelectedType(prev => prev === editingFault?.system ? prev : editingFault?.system);
    setSelectedSubtype(prev => prev === editingFault?.faultType ? prev : editingFault?.faultType);
    setExecuteLater(prev => prev === !!editingFault?.executeLater ? prev : !!editingFault?.executeLater);
    setDurationChecked(prev => prev === !!editingFault?.duration ? prev : !!editingFault?.duration);
    setDurationValue(prev => prev === (editingFault?.duration || initialDuration) ? prev : (editingFault?.duration || initialDuration));
    setStartTime(prev => prev === (editingFault?.faultExecutionTime ? editingFault?.faultExecutionTime * 1000 : currentSimulationTime) ? prev : (editingFault?.faultExecutionTime ? editingFault?.faultExecutionTime * 1000 : currentSimulationTime));

    const usesPlanSessionGate = faultPlanEditSessionKey !== undefined;
    const nextKey = usesPlanSessionGate ? faultPlanEditSessionKey : null;
    const isNewEditSession =
      !usesPlanSessionGate || lastFaultPlanSessionKeyRef.current !== nextKey;

    if (usesPlanSessionGate && isNewEditSession) {
      lastFaultPlanSessionKeyRef.current = nextKey ?? null;
    }

    if (isNewEditSession && onSelectionChange) {
      onSelectionChange(editingFault?.system as string, editingFault?.faultType as string);
    }

    if (isNewEditSession && faultDetailsLocal && precomputedFlattenedSchema) {
      const { system, faultType } = editingFault as any;
      const schemaFields = precomputedFlattenedSchema?.[system]?.[faultType] || [];

      const mappedValues: Record<string, any> = {};
      schemaFields?.forEach((field: any) => {
        if (faultDetailsLocal[field.originalKey] !== undefined) {
          mappedValues[field.name] = faultDetailsLocal[field.originalKey] as any;
        }
      });

      methods.reset(mappedValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingFault, faultPlanEditSessionKey, precomputedFlattenedSchema, initialDuration]);

  // Effect 3: Reset form fields when type/subtype changes (but preserve executeLater)
  useEffect(() => {
    if (!editingFault && (selectedType || selectedSubtype)) {
      // Reset duration to unchecked when switching subtype/type.
      // If AllowedTriggerFields disables duration, keep it unchecked.
      setDurationChecked(false);
      setStartTime(currentSimulationTime);
      setDurationValue(initialDuration);
    }
  }, [selectedType, selectedSubtype, editingFault, initialDuration]);

  // Effect 4: Update start time when executeLater is enabled
  useEffect(() => {
    if (!editingFault && executeLater) {
      setStartTime(currentSimulationTime);
    }
  }, [executeLater, editingFault]);

  // Effect 5: Reset dynamic fields when their dependency values change
  useEffect(() => {
    dynamicFields.forEach((dynamicField: any) => {
      if (!dynamicField.name || !dynamicField.dependencyField?.originalKey) return;

      // Find the actual dependency field to get its value
      const depField = (visibleFields || fields)?.find((f: any) =>
        f.originalKey === dynamicField.dependencyField.originalKey
      );

      if (!depField?.name) return;

      const dependencyValue = dependencyFieldValues[dynamicField.name];
      const currentDynamicValue = methods.getValues(dynamicField.name);
      const dependencyKey = String(dependencyValue);
      const optionsForValue = dynamicField.dynamicOptions?.[dependencyKey];

      // If dependency value changes, reset dynamic field if current value is invalid
      if (dependencyValue !== null && dependencyValue !== undefined && optionsForValue) {
        if (currentDynamicValue !== undefined && currentDynamicValue !== null) {
          const validEnum = optionsForValue.enum || [];
          const valueMatches = validEnum.some((v: any) => v == currentDynamicValue);
          if (!valueMatches) {
            methods.setValue(dynamicField.name, null);
          }
        }
      } else if (dependencyValue === null || dependencyValue === undefined) {
        // Reset dynamic field if dependency is cleared
        methods.setValue(dynamicField.name, null);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependencyFieldValues, dynamicFields, visibleFields, fields]);

  // ==============================================
  // ENHANCED RENDER LOGIC FOR DASHBOARD MODES
  // ==============================================

  // Determines if executeLater checkbox should be shown based on dashboard mode
  const showExecuteLaterCheckbox = useMemo(() => {
    if (dashboardMode === 'design') return false;
    if (dashboardMode === 'scenarioCreation') return false;
    if (dashboardMode === 'simulation') return true;
    return true;
  }, [dashboardMode]);

  // Determines if start time picker should be shown based on dashboard mode and executeLater
  const showStartTime = useMemo(() => {
    if (dashboardMode === 'scenarioCreation') return true;
    if (dashboardMode === 'simulation') return executeLater;
    return executeLater;
  }, [dashboardMode, executeLater]);

  // Determines if duration section should be shown based on dashboard mode and allowed fields
  const showDurationSection = useMemo(() => {
    const durationAllowed = allowedTriggerFields?.duration !== false;
    const modeAllows = dashboardMode === 'scenarioCreation' || dashboardMode === 'simulation';

    // In simulation mode, only show duration when executeLater is checked
    if (dashboardMode === 'simulation') {
      return modeAllows && durationAllowed && executeLater;
    }

    return modeAllows && durationAllowed;
  }, [dashboardMode, allowedTriggerFields, executeLater]);

  // ==============================================
  // EVENT HANDLERS
  // ==============================================

  // Handles system type selection change and resets form
  const handleTypeChange = (selected: any) => {
    const newType = selected?.value || '';
    setSelectedType(newType);
    setSelectedSubtype('');
    methods.reset();
    if (onSelectionChange) onSelectionChange(newType, '');
  };

  // Handles fault subtype selection change and resets form
  const handleSubtypeChange = (selected: any) => {
    const newSubtype = selected?.value || '';
    setSelectedSubtype(newSubtype);
    methods.reset();
    if (onSelectionChange) onSelectionChange(selectedType, newSubtype);
  };

  // Handles start time picker value change
  const handleStartTimeChange = (val: Dayjs | number | null) => {
    if (val) {
      const timeMs = typeof val === 'number' ? val : val.valueOf();
      setStartTime(timeMs);
    }
  };


  // Handles duration input change, validates integer-only input and prevents zero
  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Remove any non-digit characters (including decimal points) to prevent float/decimal numbers
    const integerValue = value.replace(/[^0-9]/g, '');
    // Handle empty string separately to allow clearing the field
    if (integerValue === '') {
      setDurationValue(0);
      return;
    }
    const parsed = parseInt(integerValue, 10);
    // Prevent 0 value
    if (parsed === 0) {
      showToastMessage('error', 'Duration must be greater than 0');
      setDurationValue(0);
      return;
    }
    // Ensure we store an integer value, not a decimal
    setDurationValue(isNaN(parsed) ? 0 : parsed);
  };

  // Handles executeLater checkbox toggle
  const handleExecuteLaterChange = (checked: boolean) => {
    setExecuteLater(checked);
  };

  // ==============================================
  // VALIDATION & SUBMISSION
  // ==============================================

  // Validates duration value when checkbox is checked
  const validateDuration = () => {
    if (durationChecked && (allowedTriggerFields?.duration !== false)) {
      if (!durationValue || durationValue === 0 || isNaN(durationValue)) {
        showToastMessage('error', 'Duration is required and must be greater than 0');
        return false;
      }
      return true
    }
  }

  // Validates start time is not in past and doesn't exceed scenario end time
  const validateDate = (timeMs: number) => {
    if (!timeMs || isNaN(timeMs)) {
      showToastMessage('error', 'Please select a valid date and time');
      return false;
    }

    if (timeMs < currentSimulationTime) {
      showToastMessage('error', 'Cannot select a past date/time');
      return false;
    }

    if (durationChecked) {
      const durationMs = durationValue * 60 * 1000;
      if (timeMs + durationMs > endTime) {
        showToastMessage('error', 'Start time + Duration cannot exceed the scenario end time');
        return false;
      }
    } else if (timeMs > endTime) {
      showToastMessage('error', 'Cannot select a date/time beyond the scenario end time');
      return false;
    }

    return true;
  };

  // Handles form submission, validates and formats data for backend
  const onSubmit = (data: any) => {
    const shouldValidateTime = dashboardMode === 'scenarioCreation' || executeLater;

    if (shouldValidateTime && !validateDate(startTime)) {
      return;
    }

    // Validate duration if checkbox is checked
    if (!validateDuration) return

    const output: any = {
      system: selectedType,
      faultType: selectedSubtype,
      executeLater: dashboardMode === 'scenarioCreation' ? true : executeLater,
    };

    // Add execution time if applicable
    if (dashboardMode === 'scenarioCreation' || executeLater) {
      output.faultExecutionTime = Math.floor(startTime / 1000);
    }

    // Add duration ONLY if allowed and checked
    if ((allowedTriggerFields?.duration !== false) && durationChecked && durationValue > 0) {
      // Ensure duration is always an integer
      output.duration = Math.floor(durationValue);
    }

    const faultDetails: any = {};

    (finalVisibleFields || visibleFields || fields).forEach((field: any) => {
      if (field.type === 'bool') return;

      const keys = field.name.split('.');
      let value: any = data;
      for (const key of keys) {
        value = value?.[key];
        if (value === undefined) break;
      }

      if (value !== undefined && value !== null && value !== '') {
        faultDetails[field.originalKey] = value;
      }
    });

    if (Object.keys(faultDetails).length > 0) {
      output.faultDetails = faultDetails;
    }

    if (postInject) {
      if (faultScheduleId !== undefined) {
        postInject(faultScheduleId, output);
      } else {
        (postInject as (data: any) => void)(output);
      }
    }
  };

  // Returns current form data in the format expected by parent component
  const getFormData = () => {
    const data = methods.getValues();

    // Validate duration if checkbox is checked
    if (!validateDuration) return

    const output: any = {
      system: selectedType,
      faultType: selectedSubtype,
      executeLater: dashboardMode === 'scenarioCreation' ? true : executeLater,
    };

    if (dashboardMode === 'scenarioCreation' || executeLater) {
      output.faultExecutionTime = Math.floor(startTime / 1000);
    }

    // Add duration ONLY if allowed and checked
    if ((allowedTriggerFields?.duration !== false) && durationChecked && durationValue > 0) {
      // Ensure duration is always an integer
      output.duration = Math.floor(durationValue);
    }

    const faultDetails: any = {};

    (finalVisibleFields || visibleFields || fields)?.forEach((field: any) => {
      if (field.type === 'bool') return;

      const keys = field.name.split('.');
      let value: any = data;
      for (const key of keys) {
        value = value?.[key];
        if (value === undefined) break;
      }

      if (value !== undefined && value !== null && value !== '') {
        faultDetails[field.originalKey] = value as any;
      }
    });

    if (Object?.keys(faultDetails)?.length > 0) {
      output.faultDetails = faultDetails;
    }

    return output;
  };

  // Handles form validation errors, shows first error message and scrolls to it
  const onError = (errors: any) => {
    // Recursively finds the first validation error in nested error object
    const findFirstError = (errorObj: any): any => {
      for (const key in errorObj as any as Record<string, any>) {
        if (errorObj[key]?.message) {
          return {
            field: key,
            message: errorObj[key]?.message
          };
        }
        if (typeof errorObj[key] === 'object') {
          const nestedError = findFirstError(errorObj[key] as any as Record<string, any>);
          if (nestedError) return nestedError;
        }
      }
      return null;
    };

    const firstError = findFirstError(errors);

    if (firstError) {
      showToastMessage('error', firstError?.message || 'Please check your input');

      const firstErrorElement = document.querySelector('.error') as HTMLElement | null;
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Generates validation rules for a field based on its type and constraints
  const getValidationRules = (field: any) => {
    const rules: any = {};

    if (field?.type !== 'bool') {
      rules.required = `${field.label} is required`;
    }

    if (field?.type === 'float' || field?.type === 'number') {
      if (field?.min !== undefined) {
        rules.min = {
          value: field?.min,
          message: `${field?.label} must be at least ${field?.min}`
        };
      }

      if (field?.max !== undefined) {
        rules.max = {
          value: field?.max,
          message: `${field?.label} must be at most ${field?.max}`
        };
      }

      rules.validate = {
        validNumber: (v: any) => {
          if (v === undefined || v === null || v === '') return true;
          return !isNaN(parseFloat(String(v))) || `${field?.label} must be a valid number`;
        },
        decimalPrecision: (v: any) => {
          if (field?.type !== 'float') return true;
          if (v === undefined || v === null || v === '') return true;
          const decimalPart = String(v)?.split('.')[1];
          return !decimalPart || decimalPart.length <= 8 || 'Maximum 8 decimal places allowed';
        }
      };
    }

    return rules;
  };

  // Resets form to initial state, clears all fields and selections
  const resetForm = () => {
    setSelectedType('');
    setSelectedSubtype('');
    setExecuteLater(getInitialExecuteLater());
    setDurationChecked(false);
    setDurationValue(0);
    setStartTime(currentSimulationTime);
    methods.reset({});
  };

  useImperativeHandle(ref, () => ({
    getFormData,
    resetForm
  }));

  // ==============================================
  // RENDER LOGIC
  // ==============================================

  const isJSMode = faultScheduleId !== undefined || current_simulation_time !== undefined;
  const timePickerValue = isJSMode ? startTime : dayjs(startTime);

  return (
    <FormProvider {...methods}>
      <div className={`form-header-df ${editingFault ? 'edit-form' : 'create-form'} ${!_.isEmpty(finalVisibleFields || visibleFields) ? 'form-section' : ''}`}>
        <div className="form-section">
          <div className="df-select-step">
            <label className="df-label">Type</label>
            <CustomSelect
              classNamePrefix="react-select"
              value={selectedType ? {
                value: selectedType,
                label: replaceUnderscoresWithSpaces(selectedType)
              } : null}
              onChange={handleTypeChange}
              options={typeOptions}
              placeholder="Select Type"
              controllHeight='32px'
              {...({} as any)}
            />
          </div>

          {selectedType && (
            <div className="df-select-step">
              <label className="df-label">Sub-Type</label>
              <CustomSelect
                classNamePrefix="react-select"
                value={selectedSubtype ? {
                  value: selectedSubtype,
                  label: replaceUnderscoresWithSpaces(selectedSubtype)
                } : null}
                onChange={handleSubtypeChange}
                options={subtypeOptions}
                placeholder="Select Subtype"
                controllHeight='32px'
                {...({} as any)}
              />
            </div>
          )}
        </div>
      </div>

      {selectedType && selectedSubtype && (
        <div className={`form-body ${editingFault ? 'edit-form' : 'create-form'}`}>
          <form
            className="df-form-container"
            onSubmit={methods.handleSubmit(onSubmit, onError)}
            noValidate
          >
            {!_.isEmpty(finalVisibleFields || visibleFields) && <div className="form-section">
              <h5 className="section-title-df mt-2">CONFIGURATION</h5>
              <div className="df-fields">
                {(finalVisibleFields || visibleFields)?.map((field: any) => {
                  const rules = getValidationRules(field);
                  const required = !!rules.required;

                  if (field?.type === 'bool') {
                    return <CheckboxField key={field?.name} name={field?.name} label={field?.label} />;
                  }

                  if (masterBooleanName && !masterBooleanValue) return null;

                  if (field?.enum) {
                    return (
                      <div className="df-field" key={field?.name}>
                        <label className="df-label" htmlFor={field?.name}>
                          {field?.label}
                          {required && <span className="required-asterisk">*</span>}
                        </label>
                        <Controller
                          name={field?.name}
                          control={methods.control}
                          rules={rules}
                          render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <>
                              <CustomSelect
                                className={`${error ? 'error' : ''}`}
                                value={getEnumFieldSelectValue(field, value)}
                                onChange={(selected) => handleEnumFieldChange(field, selected, onChange)}
                                options={field?.valuesLabel?.map((label, index) => ({
                                  label,
                                  value: field?.enum?.[index]
                                }))}
                                placeholder={`${field?.label}`}
                                classNamePrefix="react-select"
                                controllHeight='32px'
                                {...({} as any)}
                              />
                              {error && <p className="error-message d-none">{error.message}</p>}
                            </>
                          )}
                        />
                      </div>
                    );
                  }

                  return field?.type === 'float' || field?.type === 'number' ?
                    <NumberField
                      key={field?.name}
                      name={field?.name}
                      label={field?.label}
                      min={field?.min}
                      max={field?.max}
                      rules={rules}
                      required={required}
                      type={field?.type}
                      unit={field?.unit}
                      mergeRegisterOnChange={dashboardMode === 'design'}
                    />
                    : <TextField
                      key={field?.name}
                      name={field.name}
                      label={field?.label}
                      rules={rules}
                      required={required}
                    />;
                })}
              </div>
            </div>}

            {dashboardMode !== 'design' &&
              <>
                <div className="form-section">
                  <h5 className="section-title-df mt-2">SCHEDULING REQUIREMENT</h5>
                  <div className="scheduling-container mb-2">
                    {showExecuteLaterCheckbox && (
                      <div className="execute-later">
                        <input
                          type="checkbox"
                          checked={executeLater}
                          onChange={e => handleExecuteLaterChange(e.target.checked)}
                          className="checkbox-recurring"
                        />
                        <span className="execute-label">Execute Later</span>
                      </div>
                    )}

                    {showStartTime && (
                      <div className="start-time mb-2">
                        <label className="df-label">Start time</label>
                        <CustomTimePicker
                          value={timePickerValue as any}
                          onChange={handleStartTimeChange}
                          minDate={isJSMode ? currentSimulationTime : dayjs(currentSimulationTime)}
                          {...({} as any)}
                        />
                      </div>
                    )}

                    {showDurationSection && (
                      <div className="duration-wrapper">
                        <div className="duration-checkbox-wrapper">
                          <input
                            type="checkbox"
                            checked={durationChecked}
                            onChange={e => setDurationChecked(e.target.checked)}
                            className="checkbox-recurring"
                          />
                          <span className="execute-label">Duration</span>
                        </div>

                        {durationChecked && (
                          <div className="dynamic-form-unit-container">
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={durationValue || ''}
                              onChange={handleDurationChange}
                              onKeyDown={validateNumberWithoutDecimal}
                              className="df-input"
                            />
                            <span className="dynamic-form-unit-label">min</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-footer-df">
                  <button type="submit" className="df-button--submit w-100 mt-2">
                    {editingFault ? 'Update Fault' : 'Inject'}
                  </button>
                </div>
              </>
            }
          </form>
        </div>
      )}
    </FormProvider>
  );
});

export default DynamicForm;